import { MessageSquare, Plus, Clock, Users, Send, BarChart3, Bell, Phone, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/client/components/ui/button';
import { Card, CardContent } from '@/client/components/ui/card';
import { OnboardingEmptyState } from '@/client/components/ui/empty-state';

export function SmsPage() {
  return (
    <div className="space-y-6 stagger-children relative">
      {/* Decorative background gradient */}
      <div className="absolute inset-0 overflow-hidden opacity-30 pointer-events-none">
        <div className="absolute -top-1/2 -right-1/4 w-[800px] h-[800px] rounded-full bg-gradient-to-br from-teal-500/20 via-cyan-500/10 to-transparent blur-3xl" />
        <div className="absolute -bottom-1/2 -left-1/4 w-[600px] h-[600px] rounded-full bg-gradient-to-tr from-cyan-500/20 via-teal-500/10 to-transparent blur-3xl" />
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 relative">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-3">
            <div className="size-10 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center shadow-lg shadow-teal-500/20">
              <MessageSquare className="size-5 text-white" />
            </div>
            SMS Campaigns
          </h1>
          <p className="text-muted-foreground mt-2 text-sm sm:text-base">
            Send text message campaigns for event reminders and urgent announcements
          </p>
        </div>
        <Button
          onClick={() => toast.info('SMS Campaigns coming soon!')}
          size="lg"
          className="rounded-xl glow gap-2 bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700"
        >
          <Plus className="size-5" />
          Create SMS Campaign
        </Button>
      </div>

      {/* Empty State */}
      <OnboardingEmptyState
        icon={MessageSquare}
        title="Send your first SMS campaign"
        steps={[
          { icon: Users, text: 'Import contacts or sync from your forms' },
          { icon: Phone, text: 'Compose your message with personalization' },
          { icon: Send, text: 'Send instantly or schedule for later' },
        ]}
        action={{
          label: 'Create your first SMS campaign',
          onClick: () => toast.info('SMS Campaigns coming soon!'),
          icon: Plus,
        }}
      />

      {/* Coming Soon Banner */}
      <Card className="rounded-2xl border-2 border-dashed border-teal-300 dark:border-teal-800 bg-gradient-to-r from-teal-50/50 to-cyan-50/50 dark:from-teal-950/20 dark:to-cyan-950/20">
        <CardContent className="flex flex-col sm:flex-row items-center gap-4 p-6">
          <div className="size-16 rounded-2xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center shadow-lg">
            <Clock className="size-8 text-white" />
          </div>
          <div className="text-center sm:text-left flex-1">
            <h3 className="text-lg font-semibold mb-1">SMS Campaigns Coming Soon</h3>
            <p className="text-sm text-muted-foreground">
              We&apos;re building SMS messaging that lets you reach your audience instantly. Perfect
              for event reminders, urgent announcements, and time-sensitive communications. Send to
              individuals or broadcast to your entire contact list.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Feature Preview */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card className="rounded-2xl py-0 border-0 bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-teal-950/30 dark:to-cyan-950/30">
          <CardContent className="flex items-center gap-4 p-4">
            <div className="size-12 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center shadow-lg shadow-teal-500/20">
              <Send className="size-6 text-white" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground font-medium">Bulk SMS</p>
              <p className="text-lg font-semibold">Campaigns</p>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-2xl py-0 border-0 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30">
          <CardContent className="flex items-center gap-4 p-4">
            <div className="size-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/20">
              <Bell className="size-6 text-white" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground font-medium">Scheduled</p>
              <p className="text-lg font-semibold">Delivery</p>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-2xl py-0 border-0 bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30">
          <CardContent className="flex items-center gap-4 p-4">
            <div className="size-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg shadow-emerald-500/20">
              <BarChart3 className="size-6 text-white" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground font-medium">Delivery</p>
              <p className="text-lg font-semibold">Analytics</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Use Cases */}
      <Card className="rounded-2xl border-0 bg-gradient-to-br from-muted/30 to-muted/10">
        <CardContent className="p-6">
          <h4 className="font-semibold mb-4">Perfect for Armenian organizations</h4>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <div className="size-2 rounded-full bg-teal-500" />
              Event reminders the day before or morning of
            </div>
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <div className="size-2 rounded-full bg-teal-500" />
              Urgent announcements for schedule changes
            </div>
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <div className="size-2 rounded-full bg-teal-500" />
              Volunteer coordination and last-minute requests
            </div>
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <div className="size-2 rounded-full bg-teal-500" />
              Two-way messaging for RSVPs and confirmations
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Integration Note */}
      <Card className="rounded-2xl border-0 bg-gradient-to-br from-primary/5 to-accent/5">
        <CardContent className="flex items-start gap-4 p-6">
          <div className="size-10 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center shrink-0">
            <Zap className="size-5 text-white" />
          </div>
          <div>
            <h4 className="font-semibold mb-1">Integrated with your other tools</h4>
            <p className="text-sm text-muted-foreground">
              SMS Campaigns will sync with your contact lists from Forms and event signups.
              Automatically send reminders when someone registers, or broadcast to segmented lists
              based on past attendance.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
